import React, { useState } from "react";
import { observer } from "mobx-react";
import { useStore } from "../../../store";
import {
  SelectChangeEvent,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import Select, { SelectProps } from "../../../components/Select";
import { TextField, Autocomplete } from "@mui/material";
import {
  SecondContainer,
  SecondBox,
  ThirdBox,
  Box,
  Typography,
} from "../styles";
import {
  getLanguageCode,
  defaultAllCode,
} from "../../Templates/LanguageCode";
import {
  defaultAllCategory,
  getCategoryCode,
} from "../../Templates/CategoryCode";
import DateRangePicker from "../../../components/DateRangePicker";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import dayjs from "dayjs";
import { useCookies, Cookies } from "react-cookie";
import moment from "moment";
import { UtcDateFormatter } from "../../../utils/dateformat/dateFormat";

const Handler = () => {
  const {
    AIReviewStore: {
      gridOption: { rowsPerPage },
      application,
      setApplication,
      status,
      setStatus,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      setLanguageChange,
      language,
      category,
      setCategoryChange,
      searchHandler,
      lastVisitedAt,
    },
  } = useStore();

  const lastViewedDisplay = lastVisitedAt
    ? UtcDateFormatter(lastVisitedAt)
    : "—";

  const recommendedOption = getCategoryCode.find(
    (c) => c.key === "Recommended"
  );
  const aiReviewCategoryOptions = recommendedOption
    ? [defaultAllCategory, recommendedOption]
    : [defaultAllCategory];

  const [btnOpen, setBtnOpen] = useState(false);
  const [cookies, setCookie] = useCookies();
  const expires = moment().add("100", "y").toDate();
  const gCookies = new Cookies();
  const getCookie = (item: string) => {
    return gCookies.get(item);
  };

  const selectProps: SelectProps = {
    width: 122,
    name: "Status",
    value: status,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setStatus(event.target.value);
    },
    defaultValue: "InReview",
    list: [
      { label: "Preparing", value: "Preparing" },
      { label: "In Review", value: "InReview" },
      { label: "Rejected", value: "Rejected" },
      { label: "Published", value: "Published" },
      { label: "Blind", value: "Blind" },
      { label: "All", value: "All" },
    ],
  };

  const selectApplicationProps: SelectProps = {
    width: 122,
    name: "Application",
    value: application,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setApplication(event.target.value);
    },
    defaultValue: "all",
    list: [
      { label: "All", value: "all" },
      { label: "KineMaster", value: "kinemaster" },
      { label: "Spring", value: "spring" },
    ],
  };

  const rangeDateProps = {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
  };
  const onKeyPressEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      searchHandler();
    }
  };

  const onChangeLang = (event: any, value: any) => {
    if (value) {
      setLanguageChange(value);
    }
  };

  const onChangeCategory = (event: any, value: any) => {
    if (value) {
      setCategoryChange(value);
    }
  };

  const handleButton = () => {
    // ai_page는 저장하지 않는다 — AI Review는 항상 page=0으로 진입한다.
    // (저장하면 데이터가 줄었을 때 범위 밖 page가 되어 DataGrid 보정 → 중복 fetch)
    setCookie("ai_rowsPerPage", rowsPerPage, { path: "/", expires });
    setCookie("ai_startDate", startDate, { path: "/", expires });
    setCookie("ai_endDate", endDate, { path: "/", expires });
    setCookie("ai_application", application, { path: "/", expires });
    setCookie("ai_status", status, { path: "/", expires });
    setCookie("ai_language", JSON.stringify(language), { path: "/", expires });
    setCookie("ai_category", JSON.stringify(category), { path: "/", expires });

    // setRows()를 직접 부르면 직전에 페이지를 넘겨둔 상태(page>0)에서 필터가
    // 바뀌어 결과가 줄었을 때 범위 밖 page로 조회 → DataGrid 보정 → 재조회가
    // 발생한다. searchHandler가 page=0으로 리셋한 뒤 조회하므로 그것을 쓴다.
    searchHandler();
    setBtnOpen(false);
  };

  const handleClose = () => {
    setApplication(
      getCookie("ai_application") ? getCookie("ai_application") : "all"
    );
    setStatus(getCookie("ai_status") ? getCookie("ai_status") : "All");

    setStartDate(
      getCookie("ai_startDate")
        ? getCookie("ai_startDate")
        : dayjs().toDate()
    );
    setEndDate(
      getCookie("ai_endDate")
        ? dayjs(getCookie("ai_endDate"), "yyyy/MM/dd").toDate()
        : dayjs().toDate()
    );

    setLanguageChange(
      getCookie("ai_language") ? getCookie("ai_language") : defaultAllCode
    );
    setCategoryChange(
      getCookie("ai_category") ? getCookie("ai_category") : defaultAllCategory
    );

    setBtnOpen(false);
  };

  return (
    <>
      <SecondContainer>
        <FilterAltIcon
          onClick={() => {
            setBtnOpen(true);
          }}
          sx={{ cursor: "pointer", color: "#6750a4" }}
        />
        <SecondBox
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <ThirdBox
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              flexWrap: "wrap",
              pt: "10px",
            }}
          >
            <Typography sx={{ color: "#79747E", mr: 0.5 }}>
              Application
            </Typography>
            <Typography sx={{ whiteSpace: "nowrap", mr: 3 }}>
              {application === "kinemaster"
                ? "KineMaster"
                : application === "spring"
                ? "Spring"
                : "All"}
            </Typography>
            <Typography sx={{ color: "#79747E", mr: 0.5 }}>Status</Typography>
            <Typography sx={{ whiteSpace: "nowrap", mr: 3 }}>
              {status}
            </Typography>
            <Typography sx={{ color: "#79747E", mr: 0.5 }}>Date</Typography>
            <Typography sx={{ whiteSpace: "nowrap", mr: 3 }}>
              {dayjs(startDate).format("YYYY-MM-DD") +
                " ~ " +
                dayjs(endDate).format("YYYY-MM-DD")}
            </Typography>
            <Typography sx={{ color: "#79747E", mr: 0.5 }}>
              Language
            </Typography>
            <Typography sx={{ whiteSpace: "nowrap", mr: 3 }}>
              {language.name}
            </Typography>
            <Typography sx={{ color: "#79747E", mr: 0.5 }}>
              Category
            </Typography>
            <Typography sx={{ whiteSpace: "nowrap", mr: 3 }}>
              {category.key}
            </Typography>
            <Typography sx={{ color: "#79747E", mr: 0.5 }}>
              Last viewed
            </Typography>
            <Typography sx={{ whiteSpace: "nowrap", mr: 3 }}>
              {lastViewedDisplay}
            </Typography>
          </ThirdBox>
        </SecondBox>
      </SecondContainer>

      <Dialog
        disableEscapeKeyDown
        open={btnOpen}
        onClose={handleClose}
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "900px",
            minWidth: "900px",
            maxHeight: "650px",
            minHeight: "650px",
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Select {...selectApplicationProps} />
            <Select {...selectProps} />
            <Autocomplete
              disablePortal
              size="small"
              id="combo-box-lang"
              value={language}
              options={[defaultAllCode].concat(getLanguageCode)}
              onChange={onChangeLang}
              onKeyPress={onKeyPressEnter}
              getOptionLabel={(option: any) => {
                if (option.name === "All") {
                  return `All`;
                } else {
                  return `${option.name} (${option.value})`;
                }
              }}
              renderInput={(params) => {
                return <TextField {...params} label="Language" />;
              }}
              isOptionEqualToValue={(option: any, value: any) =>
                option.name === value.name && option.value === value.value
              }
              sx={{
                "& .MuiFormControl-root.MuiTextField-root": {
                  width: 240,
                  mr: "4px",
                },
              }}
            />
            <Autocomplete
              disablePortal
              size="small"
              id="combo-box-category"
              value={category}
              options={aiReviewCategoryOptions}
              onChange={onChangeCategory}
              onKeyPress={onKeyPressEnter}
              getOptionLabel={(option: any) => {
                return option.key === "All" ? "All" : option.key;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Category" />
              )}
              isOptionEqualToValue={(option: any, value: any) =>
                option.key === value.key
              }
              sx={{
                "& .MuiFormControl-root.MuiTextField-root": {
                  width: 180,
                  mr: "4px",
                },
              }}
            />
          </Box>

          <DialogContent
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              pl: 0,
              pt: 1,
            }}
          >
            <DateRangePicker {...rangeDateProps} />
            <Typography sx={{ color: "#79747E", mt: 2, fontSize: "0.875rem" }}>
              Last viewed: {lastViewedDisplay}
            </Typography>
          </DialogContent>
        </DialogContent>
        <DialogActions style={{ justifyContent: "center" }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleButton}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default observer(Handler);
